// frontend/App.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavigationContainer } from "@react-navigation/native";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { ActivityIndicator, View, Text, Platform, useWindowDimensions } from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";

import AITutorApp from "./app/AITutorApp";
import LoginScreen from "./app/LoginScreen";
import CreateTestQuestionsPageNew2 from "./app/CreateTestQuestionsPageNew2";
import TestPublishPage from "./app/TestPublishPage";
import ForgotPasswordScreen from "./app/ForgotPasswordScreen";
import Register from "./app/Register";
import UpgradePlanScreen from "./app/UpgradePlanScreen";
import PaymentSuccessScreen from "./app/PaymentSuccessScreen";
import PaymentCancelScreen from "./app/PaymentCancelScreen";
import CollectionBuilderScreen from "./app/CollectionBuilderScreen";
import CollectionViewerScreen from "./app/CollectionViewerScreen";
import ManageStudentsScreen from "./app/ManageStudentsScreen"; // BDD 11/7/2025
import InternalAdminPanel from "./app/InternalAdminPanel";
import DavinciScannerScreen from "./app/screens/DavinciScannerScreen"; // BDD 2026-06-09 Da Vinci kiosk
import DavinciCertWallScreen from "./app/screens/DavinciCertWallScreen"; // BDD 2026-06-14 Da Vinci certificate wall
import { FASTAPI_BASE_URL } from "./app/config";
import {
  isRestrictedUser,
  isScreenAvailable,
  getInitialAuthRoute,
  getNavigatorKey,
} from "./app/navigation/authScreens";

import { AuthProvider, useAuth } from "./app/AuthContext";
import { NotificationProvider } from "./app/NotificationContext";
import { ThemeProvider } from "./app/contexts/ThemeContext";
import PocketGitaBook_v5 from "./app/PocketGitaBook_v5";
import PYQReview from "./app/PYQReview";

// BDD 08/20/2026 - AI EXHIBIT RELATED - AI Explained museum kiosk
import AIExhibitApp from "./app/exhibit/AIExhibitApp";
import AIExhibitV2App from "./app/exhibit/v2/AIExhibitV2App";
import { getFeatureFlags } from "./app/features";

const Stack = createNativeStackNavigator();


// BDD 10/06/2026 - SBS projector auth/session shell.
//
// AITutor owns its proven SBS compositor after authentication. This small shell
// covers only unauthenticated/full-session surfaces (Login, Forgot Password,
// Self Register, and auth-loading states) so a logout/session expiry never drops
// the projector back to a single full-width image.
//
// Important: this renders ONE logical UI tree and duplicates the raster with an
// SVG filter. We intentionally do not mount two LoginScreen instances, so form
// state, focus, Google login, validation, and navigation remain single-source.
const SBS3D_AUTH_FILTER_ID = "saras-sbs3d-auth-duplicate";
const SBS3D_AUTH_ROOT_ID = "saras-sbs3d-auth-root";

const Sbs3dAuthShell = ({ enabled, children }) => {
  const { width, height } = useWindowDimensions();
  const savedDomStylesRef = useRef(null);

  // Save/restore the document styles for the lifetime of the auth SBS shell.
  useEffect(() => {
    if (!enabled || Platform.OS !== "web" || typeof document === "undefined") return;

    const body = document.body;
    const html = document.documentElement;
    const root = document.getElementById(SBS3D_AUTH_ROOT_ID);
    if (!body || !html || !root) return;

    savedDomStylesRef.current = {
      body: {
        transform: body.style.transform,
        transformOrigin: body.style.transformOrigin,
        width: body.style.width,
        height: body.style.height,
        filter: body.style.filter,
        WebkitFilter: body.style.WebkitFilter,
        overflow: body.style.overflow,
        cursor: body.style.cursor,
        background: body.style.background,
      },
      html: {
        overflow: html.style.overflow,
        background: html.style.background,
      },
      root: {
        transform: root.style.transform,
        transformOrigin: root.style.transformOrigin,
        width: root.style.width,
        height: root.style.height,
        filter: root.style.filter,
        WebkitFilter: root.style.WebkitFilter,
        overflow: root.style.overflow,
      },
    };

    return () => {
      const saved = savedDomStylesRef.current;
      if (!saved) return;
      Object.assign(body.style, saved.body);
      Object.assign(html.style, saved.html);
      Object.assign(root.style, saved.root);
      savedDomStylesRef.current = null;
    };
  }, [enabled]);

  // Same flat-SBS geometry used by AITutor: render once, duplicate one logical
  // viewport-width to the right, then compress the two-eye raster into the
  // physical left/right halves of the browser framebuffer.
  useEffect(() => {
    if (!enabled || Platform.OS !== "web" || typeof document === "undefined") return;

    const body = document.body;
    const html = document.documentElement;
    const root = document.getElementById(SBS3D_AUTH_ROOT_ID);
    if (!body || !html || !root) return;

    html.style.overflow = "hidden";
    html.style.background = "#000";

    body.style.width = "100vw";
    body.style.height = "100vh";
    body.style.background = "#000";
    body.style.overflow = "hidden";
    body.style.transform = "none";
    body.style.transformOrigin = "0 0";
    body.style.filter = "none";
    body.style.WebkitFilter = "none";
    body.style.cursor = "auto";

    root.style.width = "100vw";
    root.style.height = "100vh";
    root.style.transformOrigin = "0 0";
    root.style.transform = "scaleX(0.5)";
    root.style.filter = `url(#${SBS3D_AUTH_FILTER_ID})`;
    root.style.WebkitFilter = `url(#${SBS3D_AUTH_FILTER_ID})`;
    root.style.overflow = "visible";
  }, [enabled, width, height]);

  if (!enabled || Platform.OS !== "web") return children;

  return (
    <View
      nativeID={SBS3D_AUTH_ROOT_ID}
      style={{ flex: 1, width: "100vw", height: "100vh", overflow: "visible" }}
    >
      <svg
        aria-hidden="true"
        width="0"
        height="0"
        style={{ position: "fixed", width: 0, height: 0, pointerEvents: "none" }}
      >
        <defs>
          <filter
            id={SBS3D_AUTH_FILTER_ID}
            x={0}
            y={0}
            width={Math.max(1, width * 2)}
            height={Math.max(1, height)}
            filterUnits="userSpaceOnUse"
            primitiveUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feOffset in="SourceGraphic" dx={0} dy={0} result="sbs3dAuthLeftEye" />
            <feOffset in="SourceGraphic" dx={width} dy={0} result="sbs3dAuthRightEye" />
            <feMerge>
              <feMergeNode in="sbs3dAuthLeftEye" />
              <feMergeNode in="sbs3dAuthRightEye" />
            </feMerge>
          </filter>
        </defs>
      </svg>
      {children}
    </View>
  );
};

const getUrlFlags = () => {
  try {
    const u = typeof window !== "undefined" ? new URL(window.location.href) : null;
    const p = u ? u.searchParams : new URLSearchParams();
    const hash = (u?.hash || "").toLowerCase(); // e.g. "#/davinci-scanner"
    const registerFlag = (p.get("register") || p.get("self_register") || p.get("signup") || "").toLowerCase();
    return {
      gita: p.get("gita") === "1",
      guest: p.get("guest") === "1",
      code: p.get("code") || null,
      verifyToken: p.get("verify_token") || null,
      pyq: p.get("pyq") === "1",
      kiosk: ["1", "true", "yes", "davinci"].includes((p.get("kiosk") || "").toLowerCase()) || hash.includes("davinci-scanner"),
      wall: ["1", "true", "yes"].includes((p.get("davinci-wall") || "").toLowerCase()) || hash.includes("davinci-certificates"),
      paper: p.get("paper") || null,
      lang: (p.get("lang") || "en").toLowerCase(),
      selfRegister: registerFlag === "1" || registerFlag === "true" || registerFlag === "yes",
      autoLogin: ["1", "true", "yes"].includes((p.get("autologin") || "").toLowerCase()),
      autoLoginToken: p.get("access_token") || null,
      autoLoginRefresh: p.get("refresh_token") || null,
      autoLoginEmail: p.get("email") || null,
      autoLoginPlan: p.get("plan_code") || null,
      autoLoginGoto: p.get("goto") || null,
      paymentSuccess: ["1", "true", "yes"].includes((p.get("payment_success") || "").toLowerCase()),
      paymentCancel: ["1", "true", "yes"].includes((p.get("payment_cancel") || "").toLowerCase()),
      paymentSessionId: p.get("session_id") || null,
      // BDD 08/20/2026 - AI EXHIBIT RELATED - AI Explained museum kiosk
      aiExhibit:
        ["1", "true", "yes"].includes(
          (p.get("ai_exhibit") || "").toLowerCase()
        ) || hash.includes("ai-exhibit"),
    };
  } catch {
    return { gita: false, guest: false, code: null, verifyToken: null, pyq: false, kiosk: false, wall: false, paper: null, lang: "en", selfRegister: false, autoLogin: false, autoLoginToken: null, autoLoginRefresh: null, autoLoginEmail: null, autoLoginPlan: null, autoLoginGoto: null, paymentSuccess: false, paymentCancel: false, paymentSessionId: null, aiExhibit: false, };
  }
};

const AppNavigator = () => {
  const { authToken, user, login, isLoading } = useAuth();
  const flags = useMemo(() => getUrlFlags(), []);
  // BDD 08/20/2026 - AI EXHIBIT RELATED - AI Explained museum kiosk
  const featureFlags = useMemo(() => getFeatureFlags(), []);
  const isAIExhibitRequested =
    featureFlags.ai_exhibit === true && flags.aiExhibit === true;

  const autoLoginDoneRef = useRef(false);
  const [paymentFlags, setPaymentFlags] = useState({
    success: flags.paymentSuccess,
    cancel: flags.paymentCancel,
    sessionId: flags.paymentSessionId,
  });

  const allowGuestGita =
    flags.gita && flags.guest && flags.code &&
    flags.code === (process.env.EXPO_PUBLIC_GITA_CODE || "");

  const isAuthed = !!authToken || allowGuestGita;

  // AI Explained can run as a public walk-up exhibit or behind the existing
  // validated login. Do not let unrelated guest modes satisfy this login gate.
  const hasAuthenticatedUser = !!authToken;
  const aiExhibitRequiresLogin =
    featureFlags.ai_exhibit_require_login === true;
  const isAIExhibit =
    isAIExhibitRequested &&
    (!aiExhibitRequiresLogin || hasAuthenticatedUser);

  const canAccessAdmin = (user) =>
    user?.is_internal_admin === true || user?.role === "partneradmin";

  const canAccessDavinciScanner = (user) =>
    user?.is_internal_admin === true ||
    [
      "davinci_kiosk",
      "moderator",
      "partneradmin",
      "admin",
      "institute_admin",
      "schooladmin",
      "superadmin",
    ].includes((user?.role || "").toLowerCase());

  useEffect(() => {
    const loadAuthToken = async () => {
      try {
        const storedToken = await AsyncStorage.getItem("authToken");
        if (storedToken) login(storedToken);
      } catch (error) {
        console.error("Error loading auth token:", error);
      }
    };
    loadAuthToken();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!flags.verifyToken) return;
    const verifyUrl = `${FASTAPI_BASE_URL}/signup/verify-email?token=${encodeURIComponent(flags.verifyToken)}`;
    window.location.replace(verifyUrl);
  }, [flags.verifyToken]);

  useEffect(() => {
    if (!flags.autoLogin || !flags.autoLoginToken) return;
    if (autoLoginDoneRef.current) return;
    autoLoginDoneRef.current = true;
    (async () => {
      try {
        if (flags.autoLoginRefresh) {
          await AsyncStorage.setItem("refresh_token", flags.autoLoginRefresh);
        }
        const email = flags.autoLoginEmail || "";
        await login(flags.autoLoginToken, { email });
        if (flags.autoLoginPlan) {
          await AsyncStorage.setItem("pending_plan_code", flags.autoLoginPlan);
        }
      } catch (e) {
        console.warn("Auto-login failed:", e);
      }
    })();
  }, [flags.autoLogin, flags.autoLoginToken, flags.autoLoginRefresh, flags.autoLoginEmail, flags.autoLoginPlan, login]);

  if (flags.verifyToken) {
    return (
      <Sbs3dAuthShell enabled={featureFlags.sbs3d === true}>
        <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
          <ActivityIndicator size="large" color="#007bff" />
          <Text style={{ marginTop: 12, color: "#334155" }}>Verifying your account...</Text>
        </View>
      </Sbs3dAuthShell>
    );
  }

  if (isLoading) {
    return (
      <Sbs3dAuthShell enabled={featureFlags.sbs3d === true}>
        <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
          <ActivityIndicator size="large" color="#007bff" />
        </View>
      </Sbs3dAuthShell>
    );
  }

  const paymentRoute = isAuthed
    ? (paymentFlags.success ? "PaymentSuccess" : (paymentFlags.cancel ? "PaymentCancel" : null))
    : null;

  // Internal admins should not be auto-dropped into Da Vinci pages on normal login,
  // but explicit URL flags should still open those screens for testing/moderation.
  const isInternalAdmin = user?.is_internal_admin === true;
  const isKiosk =
    canAccessDavinciScanner(user) &&
    (flags.kiosk || (!isInternalAdmin && (user?.role || "").toLowerCase() === "davinci_kiosk"));

  // BDD 2026-06-14 — route the Da Vinci certificate wall when its flag is present
  const isWall = canAccessDavinciScanner(user) && flags.wall;

  // WP7 (AIT-291): expired-subscription users only get the upgrade/payment screens.
  const restricted = isRestrictedUser(isAuthed, user);
  const allow = (name) => isScreenAvailable(name, restricted);

  const defaultInitial = isAIExhibit   // BDD 08/20/2026 - AI EXHIBIT RELATED - AI Explained museum kiosk
    ? "AIExhibit"
    : paymentRoute
    ? paymentRoute
    : (isAuthed && flags.autoLoginGoto === "upgrade")
    ? "UpgradePlan"
    : isAuthed
    ? (isWall ? "DavinciCertWall" : (isKiosk ? "DavinciScanner" : (flags.pyq ? "PYQReview" : (flags.gita ? "PocketGitaBook_v5" : "AITutor"))))
    : (flags.selfRegister ? "SelfRegister" : "Login");
  const initial = getInitialAuthRoute({ restricted, defaultRoute: defaultInitial });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!flags.selfRegister) return;
    try {
      const url = new URL(window.location.href);
      ["register", "self_register", "signup"].forEach((k) => url.searchParams.delete(k));
      const next = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, "", next);
    } catch (e) {
      console.warn("Failed to clean register query params:", e);
    }
  }, [flags.selfRegister]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!flags.paymentSuccess && !flags.paymentCancel && !flags.autoLogin) return;
    try {
      const url = new URL(window.location.href);
      ["payment_success", "payment_cancel", "session_id", "autologin", "access_token", "refresh_token", "plan_code", "goto", "email"].forEach((k) => url.searchParams.delete(k));
      const next = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, "", next);
    } catch (e) {
      console.warn("Failed to clean payment query params:", e);
    }
    if (flags.paymentSuccess || flags.paymentCancel) {
      setPaymentFlags({ success: false, cancel: false, sessionId: null });
    }
  }, [flags.paymentSuccess, flags.paymentCancel, flags.autoLogin]);

  return (
    <Sbs3dAuthShell
      enabled={featureFlags.sbs3d === true && !isAuthed && !isAIExhibit}
    >
      <NavigationContainer>
      <Stack.Navigator
        key={getNavigatorKey(authToken, restricted)}
        initialRouteName={initial}
      >
        {featureFlags.ai_exhibit &&
          (!aiExhibitRequiresLogin || hasAuthenticatedUser) && (
          <Stack.Screen
            name="AIExhibit"
            options={{ headerShown: false }}
          >
            {() =>
              featureFlags.ai_exhibit_v2 ? (
                <AIExhibitV2App
                  authenticatedUserId={hasAuthenticatedUser ? (user?.email || null) : null}
                />
              ) : (
                <AIExhibitApp
                  authenticatedUserId={
                    aiExhibitRequiresLogin && hasAuthenticatedUser
                      ? (user?.email || null)
                      : null
                  }
                />
              )
            }

          </Stack.Screen>
        )}
        {isAuthed ? (
          <>
            {/* BDD 2026-06-09 — Da Vinci kiosk scanner (gated server-side too) */}
            {allow("DavinciScanner") && canAccessDavinciScanner(user) && (
              <Stack.Screen name="DavinciScanner" component={DavinciScannerScreen} options={{ headerShown: false }} />
            )}
            {/* BDD 2026-06-14 — Da Vinci certificate wall (gated server-side too) */}
            {allow("DavinciCertWall") && canAccessDavinciScanner(user) && (
              <Stack.Screen name="DavinciCertWall" component={DavinciCertWallScreen} options={{ headerShown: false }} />
            )}
            {allow("AITutor") && (
            <Stack.Screen name="AITutor" component={AITutorApp} options={{ headerShown: false }} />
            )}
            {allow("PocketGitaBook_v5") && (
            <Stack.Screen name="PocketGitaBook_v5" component={PocketGitaBook_v5} options={{ headerShown: false }} />
            )}
            {allow("PYQReview") && (
            <Stack.Screen name="PYQReview" options={{ headerShown: false }}>
              {() => <PYQReview paperId={flags.paper} lang={flags.lang} />}
            </Stack.Screen>
            )}
            {allow("TestQuestions") && (
            <Stack.Screen name="TestQuestions" component={CreateTestQuestionsPageNew2} options={{ headerShown: true, title: "Test Questions" }} />
            )}
            {allow("PublishTests") && (
            <Stack.Screen name="PublishTests" component={TestPublishPage} options={{ headerShown: true, title: "Publish Tests" }} />
            )}
            {allow("Collections") && (
            <Stack.Screen name="Collections" component={CollectionBuilderScreen} options={{ headerShown: false }} />
            )}
            {allow("CollectionViewer") && (
            <Stack.Screen name="CollectionViewer" component={CollectionViewerScreen} options={{ headerShown: false }} />
            )}
            <Stack.Screen name="UpgradePlan" component={UpgradePlanScreen} options={{ headerShown: true, title: "Upgrade Plan" }} />
            <Stack.Screen
              name="PaymentSuccess"
              component={PaymentSuccessScreen}
              initialParams={{ sessionId: flags.paymentSessionId }}
              options={{ headerShown: false }}
            />
            <Stack.Screen
              name="PaymentCancel"
              component={PaymentCancelScreen}
              options={{ headerShown: false }}
            />
            {/* NEW BDD 11/7/2025 */}
            {allow("ManageStudents") && (
            <Stack.Screen
              name="ManageStudents"
              component={ManageStudentsScreen}
              options={{ headerShown: true, title: "Manage Students" }}
            />
            )}
            {canAccessAdmin(user) && allow("InternalAdmin") && (
              <Stack.Screen
                name="InternalAdmin"
                component={InternalAdminPanel}
                options={{ headerShown: true, title: "Admin Panel" }}
              />
            )}

          </>
        ) : (
          <>
            <Stack.Screen name="Login" component={LoginScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ForgotPassword" component={ForgotPasswordScreen} options={{ title: "Forgot Password" }} />
            <Stack.Screen name="SelfRegister" component={Register} options={{ headerShown: false, title: "Register now" }} />
          </>
        )}
      </Stack.Navigator>
      </NavigationContainer>
    </Sbs3dAuthShell>
  );
};

function Root() {
  const { isLoading } = useAuth();
  const featureFlags = useMemo(() => getFeatureFlags(), []);

  if (isLoading) {
    return (
      <Sbs3dAuthShell enabled={featureFlags.sbs3d === true}>
        <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
          <ActivityIndicator size="large" color="#007bff" />
        </View>
      </Sbs3dAuthShell>
    );
  }
  return <AppNavigator />;
}

export default function App() {
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.onUnityExit = () => window.dispatchEvent(new Event("unityExit"));
      return () => { window.onUnityExit = null; };
    }
  }, []);
  return (
    <SafeAreaProvider>
      <AuthProvider>
        <NotificationProvider>
          <ThemeProvider>
            <Root />
          </ThemeProvider>
        </NotificationProvider>
      </AuthProvider>
    </SafeAreaProvider>
  );
}




// import React, { useState, useEffect, useMemo } from "react";
// import { NavigationContainer } from "@react-navigation/native";
// import { createNativeStackNavigator } from "@react-navigation/native-stack";
// import AsyncStorage from "@react-native-async-storage/async-storage";
// import { ActivityIndicator, View } from "react-native";

// import AITutorApp from "./app/AITutorApp";
// import LoginScreen from "./app/LoginScreen";
// import CreateTestQuestionsPageNew2 from "./app/CreateTestQuestionsPageNew2";
// import TestPublishPage from "./app/TestPublishPage";
// import ForgotPasswordScreen from "./app/ForgotPasswordScreen";
// import RegisterScreen from "./app/RegisterScreen"; // BDD 9/7/2025
// import CollectionBuilderScreen from "./app/CollectionBuilderScreen";
// import CollectionViewerScreen from "./app/CollectionViewerScreen";

// import { AuthProvider, useAuth } from "./app/AuthContext";
// import PocketGitaBook_v5 from "./app/PocketGitaBook_v5";
// import PYQReview from "./app/PYQReview";

// const Stack = createNativeStackNavigator();

// // --- URL flag helpers ---
// const getUrlFlags = () => {
//   try {
//     const u = typeof window !== "undefined" ? new URL(window.location.href) : null;
//     const p = u ? u.searchParams : new URLSearchParams();
//     return {
//       gita: p.get("gita") === "1",
//       guest: p.get("guest") === "1",
//       code: p.get("code") || null,
//     };
//   } catch {
//     return { gita: false, guest: false, code: null };
//   }
// };

// const AppNavigator = () => {
//     const { authToken, login, isLoading } = useAuth(); // ✅ Get login & loading state
//     const flags = getUrlFlags();
//     const allowGuestGita = flags.gita && flags.guest && flags.code &&
//       flags.code === (process.env.EXPO_PUBLIC_GITA_CODE || "");
//     const isAuthed = !!authToken || allowGuestGita;

//     // const linking = useMemo(() => ({
//     //   prefixes: [typeof window !== "undefined" ? window.location.origin : "/"],
//     //   config: {
//     //    screens: {
//     //       AITutor: "tutor",
//     //       PocketGitaBook_v5: "gita",
//     //       Login: "login",
//     //       // (others can stay unmapped)
//     //     },
//     //   },
//     // }), []);

//     useEffect(() => {
//         // ✅ Load auth token from AsyncStorage on app start
//         const loadAuthToken = async () => {
//             try {
//                 const storedToken = await AsyncStorage.getItem("authToken");
//                 if (storedToken) {
//                     login(storedToken); // ✅ Restore login state
//                 }
//             } catch (error) {
//                 console.error("Error loading auth token:", error);
//             }
//         };
//         loadAuthToken();
//     }, []);

//     // ✅ Show loading screen while checking AsyncStorage
//     if (isLoading) {
//         return (
//             <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
//                 <ActivityIndicator size="large" color="#007bff" />
//             </View>
//         );
//     }

//     return (
//         <NavigationContainer>
//                 <Stack.Navigator
//                     key={authToken ? "auth" : "guest"}                    // force remount on auth change
//                     initialRouteName={isAuthed ? (flags.gita ? "PocketGitaBook_v5" : flags.pyq ? "PYQReview" : AITutor") : "Login"}
//                 >

//                 {isAuthed  ? (
//                         <>
//                         <Stack.Screen name="AITutor" component={AITutorApp} options={{ headerShown: false }} />
//                         <Stack.Screen name="PocketGitaBook_v5" component={PocketGitaBook_v5} options={{ headerShown: false }} />
//                         <Stack.Screen name="PYQReview" component={PYQReview} options={{ headerShown: false }} />
//                         <Stack.Screen name="TestQuestions" component={CreateTestQuestionsPageNew2} options={{ headerShown: true, title: "Test Questions" }} />
//                         <Stack.Screen name="PublishTests" component={TestPublishPage} options={{ headerShown: true, title: "Publish Tests" }} />
//                         <Stack.Screen
//                         name="Collections"
//                         component={CollectionBuilderScreen}
//                         options={{ headerShown: false }}
//                         />
//                         <Stack.Screen
//                         name="CollectionViewer"
//                         component={CollectionViewerScreen}
//                         options={{ headerShown: false }}
//                         />
//                         </>
//               ) : (
//                    <>
//                     <Stack.Screen name="Login" component={LoginScreen} options={{ headerShown: false }} />
//                     <Stack.Screen name="ForgotPassword" component={ForgotPasswordScreen} options={{ title: "Forgot Password" }} />
//                     <Stack.Screen name="Register" component={RegisterScreen} />
//                    </>
//                 )}
//             </Stack.Navigator>
//         </NavigationContainer>
//     );
// };
// // waits for your AuthProvider’s isLoading flag before ever mounting the nav.

// function Root() {
//     const { isLoading } = useAuth();
//     if (isLoading) {
//       return (
//         <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
//           <ActivityIndicator size="large" color="#007bff" />
//         </View>
//       );
//     }
//     return <AppNavigator />;
//   }
// export default function App() {
//     // ✔️ This effect runs once on mount
//     useEffect(() => {
//         // Expose a global hook that Android can invoke
//         window.onUnityExit = () => {
//         // fire a custom event — your inner component will pick this up, which will then call
//         // call your existing cleanup logic:
//         // e.g. the same code you run when you close the PWO inside React
//             window.dispatchEvent(new Event("unityExit"));
//         };

//         // Optional: clean up if this component ever unmounts
//         return () => {
//         window.onUnityExit = null;
//         };
//     }, []);
//     return (
//         <AuthProvider>
//             <Root />
//         </AuthProvider>
//     );
// }

